import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
  KeyboardEvent,
  ChangeEvent,
} from 'react'
import styled from 'styled-components'

import { getEmojisListBySearch, getUsersListBySearch } from '@sketch/utils'
import { useBreakpoint } from '../Breakpoint'

import { useTriggerDetection } from './hooks/useTriggerDetection'
import { useSuggestionsKeyboard } from './hooks/useSuggestionsKeyboard'
import { SuggestionsPopover } from './SuggestionsPopover'
import { SuggestionItem, TRIGGERS } from './types'
import { Wrapper, StyledTextArea } from './SuggestionsInput.styles'

// Regex to match mention markdown: @[Display Name](user:id)
const MENTION_REGEX = /@\[([^\]]+)\]\(user:([^)]+)\)/g

/**
 * Parse markdown mentions and extract display text + mentions map
 * Input: "Hello @[John](user:123) and @[Jane](user:456)"
 * Output: { displayText: "Hello @John and @Jane", mentions: Map { "John" => "123", "Jane" => "456" } }
 */
const parseMarkdownToDisplay = (
  markdown: string
): { displayText: string; mentions: Map<string, string> } => {
  const mentions = new Map<string, string>()

  const displayText = markdown.replace(MENTION_REGEX, (_, name, id) => {
    mentions.set(name, id)
    return `@${name}`
  })

  return { displayText, mentions }
}

/**
 * Escape special regex characters in a string
 */
const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Convert display text back to markdown using mentions map
 * Input: "Hello @John", Map { "John" => "123" }
 * Output: "Hello @[John](user:123)"
 */
const convertDisplayToMarkdown = (
  displayText: string,
  mentions: Map<string, string>
): string => {
  let result = displayText

  // Sort by name length descending to handle overlapping names correctly
  // e.g., "@John Smith" should be replaced before "@John"
  const sortedMentions = Array.from(mentions.entries()).sort(
    (a, b) => b[0].length - a[0].length
  )

  for (const [name, id] of sortedMentions) {
    // Match @Name followed by non-word character or end of string
    const pattern = new RegExp(`@${escapeRegex(name)}(?![\\w])`, 'g')
    result = result.replace(pattern, `@[${name}](user:${id})`)
  }

  return result
}

export interface SuggestionsInputProps {
  name?: string
  value: string
  placeholder?: string
  inputRef?: React.RefObject<HTMLTextAreaElement | null>
  mentionableUsers: SuggestionItem[]
  onChange: (event: { target: { value: string } }) => void
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  className?: string
}

const SuggestionsInputUnstyled: React.FC<SuggestionsInputProps> = ({
  name,
  value,
  placeholder = 'Write a comment...',
  inputRef: externalInputRef,
  mentionableUsers,
  onChange,
  onKeyDown,
  className,
}) => {
  const internalInputRef = useRef<HTMLTextAreaElement>(null)
  const inputRef = externalInputRef || internalInputRef

  // Parse the incoming markdown value to get display text and mentions
  const { displayText, mentions: parsedMentions } = useMemo(
    () => parseMarkdownToDisplay(value),
    [value]
  )

  // Track mentions (merge parsed mentions with new ones added during editing)
  const [mentions, setMentions] = useState<Map<string, string>>(() => new Map())

  // Sync parsed mentions into state when value changes externally
  useEffect(() => {
    setMentions(prev => {
      const merged = new Map(prev)
      parsedMentions.forEach((id, name) => merged.set(name, id))
      return merged
    })
  }, [parsedMentions])

  const [caretPosition, setCaretPosition] = useState(0)
  const [closedTriggerIndex, setClosedTriggerIndex] = useState<number | null>(
    null
  )

  const isDesktop = useBreakpoint('sm')

  // Detect active trigger (using display text, not markdown)
  const trigger = useTriggerDetection(
    displayText,
    caretPosition,
    TRIGGERS,
    isDesktop
  )

  // Check if the trigger was manually closed (user pressed Escape)
  const isManuallyClosedTrigger =
    trigger !== null && trigger.triggerIndex === closedTriggerIndex

  // Get filtered suggestions based on trigger type
  const suggestions = useMemo<SuggestionItem[]>(() => {
    if (!trigger || isManuallyClosedTrigger) {
      return []
    }

    if (trigger.type === 'user') {
      return getUsersListBySearch(mentionableUsers, trigger.query.toLowerCase())
    }

    if (trigger.type === 'emoji') {
      return getEmojisListBySearch(trigger.query.toLowerCase())
    }

    return []
  }, [trigger, mentionableUsers, isManuallyClosedTrigger])

  const showPopover =
    trigger !== null && !isManuallyClosedTrigger && suggestions.length > 0

  // Handle suggestion selection
  const handleSelect = useCallback(
    (item: SuggestionItem) => {
      if (!trigger) return

      const beforeTrigger = displayText.substring(0, trigger.triggerIndex)
      const afterCaret = displayText.substring(caretPosition)

      // Build updated mentions map (needed for immediate conversion)
      const updatedMentions = new Map(mentions)
      let displayReplacement: string

      if (trigger.type === 'user' && item.display) {
        displayReplacement = `@${item.display} `
        updatedMentions.set(item.display, item.id)
        setMentions(updatedMentions)
      } else {
        displayReplacement = `${item.display} `
      }

      // Build new display text and convert to markdown
      const newDisplayText = beforeTrigger + displayReplacement + afterCaret
      const newMarkdownValue = convertDisplayToMarkdown(
        newDisplayText,
        updatedMentions
      )

      onChange({ target: { value: newMarkdownValue } })

      // Calculate new caret position (after the insertion in display text)
      const newCaretPosition = beforeTrigger.length + displayReplacement.length

      // Set caret position after React updates the value
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.setSelectionRange(newCaretPosition, newCaretPosition)
          inputRef.current.focus()
        }
      }, 0)

      setClosedTriggerIndex(null)
    },
    [trigger, displayText, caretPosition, onChange, inputRef, mentions]
  )

  // Handle closing the popover
  const handleClose = useCallback(() => {
    if (trigger) {
      setClosedTriggerIndex(trigger.triggerIndex)
    }
  }, [trigger])

  // Keyboard navigation
  const { selectedIndex, handleKeyDown: handleSuggestionsKeyDown } =
    useSuggestionsKeyboard({
      suggestions,
      isActive: showPopover,
      onSelect: handleSelect,
      onClose: handleClose,
    })

  // Update caret position on input events
  const updateCaretPosition = useCallback(() => {
    if (inputRef.current) {
      setCaretPosition(inputRef.current.selectionStart ?? 0)
    }
  }, [inputRef])

  // Handle input change
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const newDisplayText = event.target.value

      // Convert display text back to markdown for storage
      const newMarkdownValue = convertDisplayToMarkdown(
        newDisplayText,
        mentions
      )

      onChange({ target: { value: newMarkdownValue } })
      // Reset closed trigger when user types something new
      setClosedTriggerIndex(null)
    },
    [onChange, mentions]
  )

  // Handle key down - combine suggestions keyboard handling with external handler
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      // First, try to handle with suggestions keyboard
      const handled = handleSuggestionsKeyDown(event)

      // If not handled by suggestions, pass to external handler
      if (!handled && onKeyDown) {
        onKeyDown(event)
      }
    },
    [handleSuggestionsKeyDown, onKeyDown]
  )

  // Update caret position after display text changes
  useEffect(() => {
    updateCaretPosition()
  }, [displayText, updateCaretPosition])

  // Auto-resize textarea to fit content (up to 6 lines, then scroll)
  useEffect(() => {
    const textarea = inputRef.current
    if (!textarea) return

    const rafId = requestAnimationFrame(() => {
      // Reset to auto first to measure true content height (allows shrinking)
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    })

    return () => cancelAnimationFrame(rafId)
  }, [displayText, inputRef])

  return (
    <Wrapper className={className}>
      <StyledTextArea
        ref={inputRef}
        name={name}
        value={displayText}
        placeholder={placeholder}
        onChange={handleChange}
        onSelect={updateCaretPosition}
        onClick={updateCaretPosition}
        onKeyDown={handleKeyDown}
        onKeyUp={updateCaretPosition}
      />
      {showPopover && trigger && (
        <SuggestionsPopover
          suggestions={suggestions}
          selectedIndex={selectedIndex}
          triggerType={trigger.type}
          onSelect={handleSelect}
          onClose={handleClose}
          searchQuery={trigger.query}
          anchorRef={inputRef}
        />
      )}
    </Wrapper>
  )
}

export const SuggestionsInput = styled(SuggestionsInputUnstyled)``
